import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays the wishlist icon + count bubble in the header.
 *
 * Mirrors cart-icon.js's shape, but reacts to WishlistUpdateEvent (localStorage-backed,
 * client-only — there is no server `cart`-style object for Liquid to read the count from, so
 * every render here is driven by the event, never by initial markup).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleText
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
  }

  /** @param {import('@theme/events').WishlistUpdateEvent} event */
  onWishlistUpdate = (event) => {
    const { count, action } = event.detail;
    // Hydration is the initial "what does localStorage say" pass on every page load —
    // animating that would make the badge pop on every navigation, not just on a real add.
    this.renderWishlistBubble(count, action !== 'hydrate');
  };

  /**
   * @param {number} count
   * @param {boolean} [animate=true]
   */
  renderWishlistBubble = async (count, animate = true) => {
    this.refs.wishlistBubbleCount.textContent = count > 0 ? String(count) : '';
    this.refs.wishlistBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', count === 0);

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.wishlistBubble.classList.add('wishlist-bubble--animating');
    await onAnimationEnd(this.refs.wishlistBubbleText);
    this.refs.wishlistBubble.classList.remove('wishlist-bubble--animating');
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
